import type {
  AuthSession,
  AnalyticsUsersResponse,
  CampaignApplicationsResponse,
  Campaign,
  CampaignRequestsResponse,
  JsonRecord,
  MeResponse,
  Product,
  RegionOption,
  CampaignResultPackage,
  ReactionContentAsset,
  ReactionContentAssetType,
  ReactionContentResponse,
} from "../types/supplier";
import type {
  SurveyDocument,
  SurveyQuestion,
} from "../../features/surveys/types";
import { env } from "../config/env";
import { apiRequest } from "./http";

const items = <T>(value: { items?: T[] }) => value.items ?? [];
const gatewayBaseUrl = () => env.supplierApiUrl.replace(/\/supplier\/?$/, "");

export const supplierApi = {
  signInWithPhone(firebaseIdToken: string) {
    return apiRequest<AuthSession & JsonRecord>("/v1/auth/supplier/phone", {
      method: "POST",
      auth: false,
      body: {
        firebaseIdToken,
        signupSource: "supplier_app",
        terms: {
          serviceTermsAgreed: true,
          privacyPolicyAgreed: true,
          agreedAt: new Date().toISOString(),
        },
      },
    });
  },

  recoverWithdrawal(firebaseIdToken: string) {
    return apiRequest<AuthSession>("/v1/auth/supplier/recover-withdrawal", {
      method: "POST",
      auth: false,
      body: {
        firebaseIdToken,
        signupSource: "supplier_app",
        terms: {
          serviceTermsAgreed: true,
          privacyPolicyAgreed: true,
          agreedAt: new Date().toISOString(),
        },
      },
    });
  },

  refresh(refreshToken: string) {
    return apiRequest<AuthSession>("/v1/auth/refresh", {
      method: "POST",
      auth: false,
      body: { refreshToken },
    });
  },

  logout(refreshToken: string) {
    return apiRequest<JsonRecord>("/v1/auth/logout", {
      method: "POST",
      auth: false,
      body: { refreshToken },
    });
  },

  me: () => apiRequest<MeResponse>("/v1/me"),
  deleteMe: () => apiRequest<JsonRecord>("/v1/me", { method: "DELETE" }),

  completeOnboarding(input: {
    contactName: string;
    contactEmail: string;
    companyName: string;
    businessNumber: string;
    representativeName: string;
    openingDate: string;
    businessAddress: string;
  }) {
    return apiRequest<JsonRecord>("/v1/supplier/onboarding", {
      method: "POST",
      body: {
        contact: {
          contactName: input.contactName,
          contactEmail: input.contactEmail,
        },
        company: {
          companyName: input.companyName,
          businessRegistrationNumber: input.businessNumber,
          representativeName: input.representativeName,
          openingDate: input.openingDate,
          businessAddress: input.businessAddress,
          productCategories: [],
        },
      },
    });
  },

  updateProfile(input: JsonRecord) {
    return apiRequest<JsonRecord>("/v1/supplier/profile", {
      method: "PATCH",
      body: input,
    });
  },

  registerPushToken(token: string) {
    return apiRequest<JsonRecord>("/v1/push-tokens", {
      method: "POST",
      body: { appType: "supplier_app", platform: "web", token },
    });
  },

  regions: (level?: number, parentCode?: string) =>
    apiRequest<{ items?: RegionOption[] }>("/v1/regions", {
      auth: false,
      query: { level, parentCode },
    }).then(items),

  products: (compact = false) =>
    apiRequest<{ items?: Product[] }>("/v1/supplier/products", {
      query: compact ? { compact: 1 } : undefined,
    }).then(items),

  createProduct: (input: JsonRecord) =>
    apiRequest<{ product: Product }>("/v1/supplier/products", {
      method: "POST",
      body: input,
    }),

  updateProduct: (productId: string, input: JsonRecord) =>
    apiRequest<JsonRecord>(`/v1/supplier/products/${productId}`, {
      method: "PATCH",
      body: input,
    }),

  uploadImage(file: File, purpose: string) {
    return apiRequest<{ file?: { publicUrl?: string } }>("/v1/files", {
      method: "POST",
      headers: {
        "content-type": file.type,
        "x-file-name": encodeURIComponent(file.name),
        "x-file-purpose": purpose,
      },
      body: file,
    }).then((data) => {
      const url = data.file?.publicUrl;
      if (!url) throw new Error("이미지 업로드 응답이 올바르지 않습니다.");
      return url;
    });
  },

  campaigns: () =>
    apiRequest<{ items?: Campaign[] }>("/v1/supplier/sample-campaigns").then(
      items,
    ),

  createCampaign: (input: JsonRecord) =>
    apiRequest<{ sampleCampaign: Campaign }>(
      "/v1/supplier/sample-campaigns",
      { method: "POST", body: input },
    ),

  updateCampaign: (campaignId: string, input: JsonRecord) =>
    apiRequest<{ sampleCampaign: Campaign }>(
      `/v1/supplier/sample-campaigns/${campaignId}`,
      { method: "PATCH", body: input },
    ),

  campaignRequests: (campaignId: string) =>
    apiRequest<CampaignRequestsResponse>(
      `/v1/supplier/sample-campaigns/${campaignId}/requests`,
    ),

  campaignApplications: (campaignId: string) =>
    apiRequest<CampaignApplicationsResponse>(
      `/v1/supplier/sample-campaigns/${campaignId}/applications`,
    ),

  reactionContent: (campaignId: string) =>
    apiRequest<ReactionContentResponse>(
      `/v1/supplier/sample-campaigns/${campaignId}/campaign-results`,
    ),

  createCampaignResultPackage: (
    campaignId: string,
    input: {
      generationPercent?: number | null;
    },
  ) =>
    apiRequest<{ package?: CampaignResultPackage | null }>(
      `/v1/supplier/sample-campaigns/${campaignId}/campaign-results/packages`,
      { method: "POST", body: input },
    ),

  createReactionContentAsset: (
    campaignId: string,
    input: {
      assetType: ReactionContentAssetType;
      generationPercent?: number | null;
    },
  ) =>
    apiRequest<{ asset?: ReactionContentAsset }>(
      `/v1/supplier/sample-campaigns/${campaignId}/reaction-content/assets`,
      { method: "POST", body: input },
    ),

  decideApplication: (
    applicationId: string,
    input: { status: "selected" | "not_selected"; reason?: string },
  ) =>
    apiRequest<JsonRecord>(
      `/v1/supplier/sample-applications/${applicationId}/decision`,
      { method: "POST", body: input },
    ),
  decideRequest: (
    sampleRequestId: string,
    input: { status: "selected" | "not_selected"; reason?: string },
  ) =>
    apiRequest<JsonRecord>(
      `/v1/supplier/sample-requests/${sampleRequestId}/decision`,
      { method: "POST", body: input },
    ),

  analyticsUsers: (input: {
    productId?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }) =>
    apiRequest<AnalyticsUsersResponse>("/v1/supplier/analytics/users", {
      query: input,
    }),

  surveyAssistant: (input: {
    prompt: string;
    product: {
      productName: string;
      brandName: string;
      category: string;
      shortDescription: string;
    };
    campaign: {
      kitType: string;
      title: string;
      sampleNotice: string;
    };
    consumerContext: { description: string };
    survey: SurveyDocument;
  }) =>
    apiRequest<{
      result: {
        assistantMessage: string;
        questions: SurveyQuestion[];
      };
      model: string;
    }>("/v1/supplier/ai-validation/survey-assistant", {
      method: "POST",
      baseUrl: gatewayBaseUrl(),
      body: input as unknown as JsonRecord,
    }),

  selectFullProductReward: (
    sampleRequestId: string,
    input: { selected: boolean; note?: string | null },
  ) =>
    apiRequest<JsonRecord>(
      `/v1/supplier/sample-requests/${sampleRequestId}/full-product-reward`,
      { method: "POST", body: input },
    ),

};
